import { Button, Col, Drawer, Row, Space, Tooltip, message } from "antd";
import { RcFile } from "antd/lib/upload";
import { useCallback, useEffect, useState } from "react";
import { UseMutationOptions } from "react-query";
import { useAdEngineActions } from "redux/assetExporter/assetExporter.slice";
import { steps } from "shared/constants/assetExporter";
import useFetchCurrConfig from "shared/hooks/assetExporter/useFetchCurrConfig";
import {
  SheetData,
  usePostGoogleSheet,
} from "shared/hooks/assetExporter/useFetchGoogleSheet";
import { useCreateFeedCron } from "shared/hooks/assetExporter/useMutateFeeds";
import useServerConfig from "shared/hooks/assetExporter/useServerConfig";
import useUploadCsv from "shared/hooks/assetExporter/useUploadCsv";
import { useAppSelector } from "shared/hooks/useAppSelector";
import { STEP } from "shared/types/assetExporter";
import styles from "./NewFeedDrawer.module.scss";
import { MapColStep } from "./newFeedDrawer/MapColStep";
import { ReviewStep } from "./newFeedDrawer/ReviewStep";
import { SchedulingStep } from "./newFeedDrawer/SchedulingStep";
import { StepsConfig } from "./newFeedDrawer/StepsConfig";
import { UploadStep } from "./newFeedDrawer/UploadStep";
import { AssetBuilderConfigStep } from "./newFeedDrawer/configureStep/AssetBuilderConfigStep";

export const NewFeedDrawer = () => {
  const { feedDrawer, isFeedDrawerOpen } = useAppSelector(
    state => state.assetExporter.sourceFeed,
  );
  const { googleSheetId, googleSheetName } = feedDrawer;
  const actions = useAdEngineActions();

  const { setNextStep, setPrevStep, setUploadedFile } = actions;
  const [customFile, setCustomFile] = useState<RcFile>();
  const isUpdate = feedDrawer.drawerMode === "UPDATE";

  const {
    data: customFilename,
    mutate: uploadCsv,
    isLoading: isUploadingCsv,
    isSuccess: isUploadSuccess,
  } = useUploadCsv();

  const {
    data: serverFilename,
    mutate: putServerConfig,
    isLoading: isPuttingConfig,
    isSuccess: isConfigSuccess,
    isError: isConfigError,
  } = useServerConfig();

  const postGoogleSheetOptions: Omit<
    UseMutationOptions<string, Error, SheetData>,
    "mutationFn"
  > = {
    onSuccess: filename => {
      setUploadedFile(filename);
      setNextStep();
    },
    onError: errorMsg => {
      message.error(errorMsg.message);
    },
  };
  const { mutate: postGoogleSheet, isLoading: isPostingGoogleSheet } =
    usePostGoogleSheet(postGoogleSheetOptions);

  const {
    mutate: createFeedCron,
    isLoading: isCreatingCron,
    isSuccess: isCronCreated,
  } = useCreateFeedCron();

  const {
    data: feedConfig,
    isLoading: isFeedConfigLoading,
    isSuccess: isFeedConfigSuccess,
  } = useFetchCurrConfig(feedDrawer.feedId, isUpdate);

  const onServerConfig = useCallback(() => {
    if (isConfigSuccess && serverFilename) {
      setUploadedFile(serverFilename);
      setNextStep();
    }
    if (isConfigError) message.error("Unable to proceed");
  }, [
    isConfigSuccess,
    setNextStep,
    setUploadedFile,
    serverFilename,
    isConfigError,
  ]);

  const onCustomConfig = useCallback(() => {
    if (isUploadSuccess && customFilename) {
      setUploadedFile(customFilename);
      setNextStep();
    }
  }, [isUploadSuccess, setNextStep, setUploadedFile, customFilename]);

  const onDrawerClose = useCallback(() => {
    if (!isFeedDrawerOpen) setCustomFile(undefined);
  }, [isFeedDrawerOpen]);

  const onImportSuccess = useCallback(() => {
    if (isCronCreated) actions.resetDrawer();
  }, [actions, isCronCreated]);

  const onFeedUpdateConfig = useCallback(() => {
    if (isFeedConfigSuccess && feedConfig) {
      actions.setFeedConfig(feedConfig);
    }
  }, [actions, feedConfig, isFeedConfigSuccess]);

  useEffect(onServerConfig, [onServerConfig]);
  useEffect(onCustomConfig, [onCustomConfig]);
  useEffect(onDrawerClose, [onDrawerClose]);
  useEffect(onImportSuccess, [onImportSuccess]);
  useEffect(onFeedUpdateConfig, [onFeedUpdateConfig]);

  const currStep = feedDrawer.step;
  const stepAction = feedDrawer.latestStep;

  const isCustomType = feedDrawer.uploadType === "custom";
  const isGoogleSheetType = feedDrawer.uploadType === "googlesheet";
  const isAssetbuilderFeed = feedDrawer.selectedType === "assetBuilder";
  const createSteps = steps
    .filter(s => !isCustomType || s !== "Scheduling")
    .filter(s => isAssetbuilderFeed || s !== "Configure");
  const updateSteps = steps
    .filter(s => s !== "Map Columns")
    .filter(s => !isCustomType || s !== "Scheduling")
    .filter(s => isAssetbuilderFeed || s !== "Configure");

  const onClick = async () => {
    if (stepAction === STEP.ONE) {
      if (isCustomType) {
        if (!!customFile) uploadCsv(customFile);
        else setNextStep();
      } else if (isGoogleSheetType) {
        postGoogleSheet({ sheetId: googleSheetId, sheetName: googleSheetName });
      } else {
        putServerConfig();
      }
    } else if (isUpdate && updateSteps[stepAction] === "Review") {
      createFeedCron(feedDrawer);
    } else if (!isUpdate && createSteps[stepAction] === "Review") {
      createFeedCron(feedDrawer);
    } else {
      setNextStep();
    }
  };

  const handlePrevStep = async () => {
    if (stepAction === STEP.ONE) return;
    setPrevStep();
  };

  const isLoading =
    isUploadingCsv ||
    isPuttingConfig ||
    isCreatingCron ||
    isFeedConfigLoading ||
    isPostingGoogleSheet ||
    feedDrawer.isProcessingPreview;

  const latestStep = feedDrawer.latestStep;
  const btnTextCreate =
    !isUpdate && createSteps[latestStep] !== "Review"
      ? "Next"
      : "Complete Import";

  const btnTextUpdate =
    isUpdate && updateSteps[latestStep] !== "Review" ? "Next" : "Complete Edit";

  return (
    <Drawer
      title={<strong>{isUpdate ? "Edit" : "Add New"} Feed</strong>}
      placement="right"
      visible={isFeedDrawerOpen}
      width="80%"
      footer={
        <Space className={styles.modalFooter}>
          <Button onClick={() => actions.resetDrawer()}>Cancel</Button>
          <div>
            {currStep !== STEP.ONE && (
              <Button onClick={handlePrevStep} className={styles.backButton}>
                Back
              </Button>
            )}
            {!!feedDrawer.errMsg ? (
              <Tooltip title={feedDrawer.errMsg}>
                <Button disabled={true} data-cy="feed-next-btn">
                  Next
                </Button>
              </Tooltip>
            ) : (
              <Button
                type="primary"
                disabled={feedDrawer.isNextBtnDisabled}
                loading={isLoading}
                onClick={onClick}
                data-cy="feed-next-btn"
              >
                {isUpdate ? btnTextUpdate : btnTextCreate}
              </Button>
            )}
          </div>
        </Space>
      }
      footerStyle={{ display: "flex", justifyContent: "flex-end" }}
      onClose={() => actions.resetDrawer()}
    >
      <Row className={styles.bodyContainer}>
        <Col span={5} className={styles.borderLine}>
          <StepsConfig />
        </Col>
        <Col span={19}>
          <div className={styles.stepsContainer}>
            {isUpdate ? (
              <>
                {currStep === STEP.ONE && (
                  <UploadStep
                    setCustomFile={setCustomFile}
                    customFile={customFile}
                  />
                )}
                {updateSteps[currStep] === "Map Columns" && <MapColStep />}
                {updateSteps[currStep] === "Configure" && (
                  <AssetBuilderConfigStep />
                )}
                {updateSteps[currStep] === "Scheduling" && <SchedulingStep />}
                {updateSteps[currStep] === "Review" && <ReviewStep />}
              </>
            ) : (
              <>
                {currStep === STEP.ONE && (
                  <UploadStep
                    key={`new_feed_drawer_key_${isFeedDrawerOpen}`}
                    setCustomFile={setCustomFile}
                    customFile={customFile}
                  />
                )}
                {createSteps[currStep] === "Map Columns" && <MapColStep />}
                {createSteps[currStep] === "Configure" && (
                  <AssetBuilderConfigStep />
                )}
                {createSteps[currStep] === "Scheduling" && <SchedulingStep />}
                {createSteps[currStep] === "Review" && <ReviewStep />}
              </>
            )}
          </div>
        </Col>
      </Row>
    </Drawer>
  );
};
